/**
 * True-up: the small corrections that make separately scanned rooms meet
 * exactly once they have been snapped together. Every room is measured with
 * some error, so where two rooms share a wall, run flush, share a doorway, or
 * have outside walls that should run in one line, their measurements disagree
 * by a fraction of an inch. True-up treats each measurement as normally
 * distributed around its true value and finds the most likely set of true
 * values: the smallest corrections, weighted by how much each measurement can
 * be trusted, under which every matched feature coincides. It then reweights
 * toward the smallest largest correction, so a mismatch is shared across the
 * cluster of measurements around it rather than pushed onto one of them.
 *
 * What it may adjust: each room's placement and size, the position and width
 * of every door, window, opening and alcove on a room's walls and its
 * alcoves' walls, and each alcove's depth. Rotations stay as placed. A wall that runs
 * straight, such as a closet flush with the room's side wall, stays straight.
 * No single correction may exceed `TRUE_UP_MAX_ADJUSTMENT`; a match that
 * would need more is left as it is and reported.
 *
 * Every length true-up changes lands on the half-inch grid, like any other
 * edit. Room placements stay free, and they close what the rounding opens.
 * Furniture, counters and other free elements move with their nearest walls,
 * so whatever stood against a wall still does.
 */
import { type FloorPlanRoom, HALF_INCH, type Point, roundToHalfInch, withFreeElementsFollowingWalls } from '@kaiizen/kai-floor-plan';
import type { AssemblyRoom } from '../assemblyModel';
import { roomSnapLines, type SnapLine } from '../snapLines';
import { minimumNormStep } from './minimumNormStep';
import { findMatches, isFeatureMatch, type Match, residual } from './trueUpMatches';
import { type Parameter, roomParameters } from './trueUpParameters';

/** The most any one measurement may move (ft). */
export const TRUE_UP_MAX_ADJUSTMENT = 2 / 12;
/** Residuals this small (ft) count as met. */
const MET = 1e-6;
/** Finite-difference step for the Jacobian (ft). */
const STEP = 1e-5;
/** Reweighting rounds that trade the least-squares corrections toward the smallest largest one. */
const SPREAD_ROUNDS = 6;
/** Per round, a measurement that barely moved grows up to 1 / SPREAD_FLOOR times cheaper than the one that moved most. */
const SPREAD_FLOOR = 0.1;

export interface TrueUpResult {
  /** The level's rooms, adjusted. Unchanged rooms are the same objects. */
  rooms: AssemblyRoom[];
  /** Matched features brought into line. */
  aligned: number;
  /** Matched features left as they were: lining them up needed more than the largest allowed correction. */
  skipped: number;
  /** The largest single correction made (ft). */
  largestAdjustment: number;
  /** Every correction made, largest first. */
  adjustments: TrueUpAdjustment[];
}

/** One correction true-up made. */
export interface TrueUpAdjustment {
  /** The room's name. */
  room: string;
  /** What changed: "moved left" (in plan view), "width", "length", "door width", "alcove depth", "alcove door position". */
  what: string;
  /** By how much (ft): signed for a length, the distance for a move. */
  by: number;
}

/** One level's true-up: its rooms, every measurement it may adjust, and each measurement as scanned. */
interface Problem {
  rooms: readonly AssemblyRoom[];
  parameters: Parameter[];
  /** Each parameter's value as scanned. */
  initial: number[];
  /** Each parameter's σ²: how freely the free solve moves it. */
  variances: number[];
  /** Where each room's parameters start: its x, then its y. */
  placement: number[];
}

/** The matches still held, and the measurements that hold them. */
interface Solution {
  matches: Match[];
  values: number[];
}

function trueUpProblem(rooms: readonly AssemblyRoom[]): Problem {
  const parameters = rooms.flatMap(({ room }, index) => roomParameters(room, index));
  return {
    rooms,
    parameters,
    initial: parameters.map(parameter => parameter.get(rooms[parameter.room].room)),
    variances: parameters.map(parameter => parameter.sigma ** 2),
    placement: rooms.map((_, index) => parameters.findIndex(parameter => parameter.room === index)),
  };
}

const applyRoom = ({ rooms, parameters }: Problem, values: number[], room: number): FloorPlanRoom =>
  parameters.reduce((current, parameter, index) => parameter.room === room ? parameter.set(current, values[index]) : current, rooms[room].room);
const apply = (problem: Problem, values: number[]): FloorPlanRoom[] => problem.rooms.map((_, room) => applyRoom(problem, values, room));
const linesOf = (problem: Problem, values: number[]): SnapLine[][] => apply(problem, values).map(roomSnapLines);
const shiftsOf = ({ placement, initial }: Problem, values: number[]): Point[] =>
  placement.map(first => ({ x: values[first] - initial[first], y: values[first + 1] - initial[first + 1] }));
const residualsOf = (problem: Problem, values: number[], matches: Match[]): number[] => {
  const lines = linesOf(problem, values), shifts = shiftsOf(problem, values);
  return matches.map(match => residual(lines, shifts, match));
};
const worstOf = (residuals: number[]) => Math.max(0, ...residuals.map(Math.abs));
const holds = (problem: Problem, matches: Match[], values: number[]) => worstOf(residualsOf(problem, values, matches)) < MET;
const largestChange = ({ initial }: Problem, values: number[]) => Math.max(0, ...values.map((value, index) => Math.abs(value - initial[index])));
const withinLimit = (problem: Problem, values: number[]) => largestChange(problem, values) <= TRUE_UP_MAX_ADJUSTMENT + MET;
const hasChanged = ({ initial }: Problem, values: number[], index: number) => Math.abs(values[index] - initial[index]) > MET;
const featureCount = (matches: Match[]) => matches.filter(isFeatureMatch).length;

/**
 * Each residual's rate of change with each parameter, by finite difference. A
 * nudge moves only its own room's walls (and, for a placement, where its faces
 * are looked for), so only that room's faces are rebuilt. A parameter with no
 * weight holds still, and its column stays zero.
 */
function jacobianOf(problem: Problem, matches: Match[], values: number[], residuals: number[], weights: number[]): number[][] {
  const lines = linesOf(problem, values);
  const jacobian = matches.map(() => new Array<number>(problem.parameters.length).fill(0));
  problem.parameters.forEach((parameter, column) => {
    if (!weights[column]) return;
    const nudged = values.map((value, index) => (index === column ? value + STEP : value));
    const nudgedLines = [...lines];
    nudgedLines[parameter.room] = roomSnapLines(applyRoom(problem, nudged, parameter.room));
    const nudgedShifts = shiftsOf(problem, nudged);
    matches.forEach((match, row) => {
      jacobian[row][column] = (residual(nudgedLines, nudgedShifts, match) - residuals[row]) / STEP;
    });
  });
  return jacobian;
}

/**
 * Gauss-Newton from `start` toward every match holding; a parameter with no
 * weight holds still. A step that leaves the matches further off is
 * halved until it doesn't.
 */
function solveFrom(problem: Problem, matches: Match[], start: number[], weights: number[]): number[] {
  let values = start;
  let residuals = residualsOf(problem, values, matches);
  for (let iteration = 0; iteration < 8 && worstOf(residuals) >= MET; iteration++) {
    const delta = minimumNormStep(jacobianOf(problem, matches, values, residuals, weights), residuals, weights);
    let scale = 1, next = values, nextResiduals = residuals;
    for (let halving = 0; halving < 6; halving++, scale /= 2) {
      next = values.map((value, index) => value + scale * delta[index]);
      nextResiduals = residualsOf(problem, next, matches);
      if (worstOf(nextResiduals) < worstOf(residuals)) break;
    }
    if (worstOf(nextResiduals) >= worstOf(residuals)) break;
    [values, residuals] = [next, nextResiduals];
  }
  return values;
}

/** The matches without the feature match farthest from holding in `values`. */
function withoutWorst(problem: Problem, matches: Match[], values: number[]): Match[] {
  const misses = residualsOf(problem, values, matches).map((miss, index) => (isFeatureMatch(matches[index]) ? Math.abs(miss) : -1));
  const worst = misses.indexOf(Math.max(...misses));
  return matches.filter((_, index) => index !== worst);
}

/**
 * Phase 1: solve freely from the measurements as scanned, leaving out the
 * matches that would need more than the largest allowed correction, the one
 * farthest from holding at a time: at the start when the solve met every
 * match but went past the limit, else in the solve.
 */
function solveWithinLimit(problem: Problem, all: Match[]): Solution {
  let matches = all, values = problem.initial;
  while (featureCount(matches)) {
    values = solveFrom(problem, matches, problem.initial, problem.variances);
    const met = holds(problem, matches, values);
    if (met && withinLimit(problem, values)) break;
    matches = withoutWorst(problem, matches, met ? problem.initial : values);
  }
  return { matches, values };
}

/**
 * Phase 2: spread the corrections. What moved most grows dearer, round by
 * round, so a cluster of measurements shares a mismatch. Keeps the best round.
 */
function spreadCorrections(problem: Problem, { matches, values }: Solution): Solution {
  let weights = problem.variances, best = values;
  for (let round = 0; round < SPREAD_ROUNDS && featureCount(matches); round++) {
    const moved = best.map((value, index) => Math.abs(value - problem.initial[index]));
    const most = Math.max(...moved);
    if (!most) break;
    weights = weights.map((weight, index) => weight * (most / Math.max(moved[index], SPREAD_FLOOR * most)));
    const candidate = solveFrom(problem, matches, problem.initial, weights);
    if (holds(problem, matches, candidate) && largestChange(problem, candidate) < largestChange(problem, best)) best = candidate;
  }
  return { matches, values: best };
}

/** A corrected length on the half-inch grid, stepped back toward the measured value if rounding took it past the largest allowed correction. */
function onGrid(value: number, measured: number): number {
  const rounded = roundToHalfInch(value);
  if (Math.abs(rounded - measured) <= TRUE_UP_MAX_ADJUSTMENT + MET) return rounded;
  return rounded + (rounded > measured ? -HALF_INCH : HALF_INCH);
}

/** The changed length still free that is nearest the half-inch grid, or -1 when none is left. */
function nearestToGrid(problem: Problem, values: number[], weights: number[]): number {
  let next = -1, nextDistance = Infinity;
  values.forEach((value, index) => {
    if (!problem.parameters[index].isLength || !weights[index] || !hasChanged(problem, values, index)) return;
    const distance = Math.abs(onGrid(value, problem.initial[index]) - value);
    if (distance < nextDistance) [next, nextDistance] = [index, distance];
  });
  return next;
}

/** Length `pinned`, put on the grid, holds there while the rest solve again around it, leaving out the matches that then can't hold within the limit. */
function solveAroundPinned(problem: Problem, { matches, values }: Solution, weights: number[], pinned: number): Solution {
  const start = values.map((value, index) => (index === pinned ? onGrid(value, problem.initial[index]) : value));
  let solved = solveFrom(problem, matches, start, weights);
  while (featureCount(matches) && !(holds(problem, matches, solved) && withinLimit(problem, solved))) {
    matches = withoutWorst(problem, matches, solved);
    solved = solveFrom(problem, matches, start, weights);
  }
  return { matches, values: solved };
}

/**
 * Phase 3: pin each changed length to the half-inch grid in turn, nearest
 * first. Lengths that haven't changed stay free, so later corrections can
 * still use them, and end exactly as measured.
 */
function pinToGrid(problem: Problem, solution: Solution): Solution {
  const weights = [...problem.variances];
  let current = solution;
  for (let next = nearestToGrid(problem, current.values, weights); next >= 0; next = nearestToGrid(problem, current.values, weights)) {
    weights[next] = 0;
    current = solveAroundPinned(problem, current, weights, next);
  }
  const { matches, values } = current;
  return {
    matches,
    values: values.map((value, index) => (problem.parameters[index].isLength && !hasChanged(problem, values, index) ? problem.initial[index] : value)),
  };
}

/** Phase 4: every correction in `values`, largest first: each room's move along each axis, then each length it changed. */
function listAdjustments(problem: Problem, values: number[]): TrueUpAdjustment[] {
  const { parameters, initial } = problem;
  const shifts = shiftsOf(problem, values);
  return problem.rooms.flatMap(({ name }, room) => {
    const { x, y } = shifts[room];
    const moved = [
      ...(Math.abs(x) > MET ? [{ room: name, what: x > 0 ? 'moved right' : 'moved left', by: Math.abs(x) }] : []),
      ...(Math.abs(y) > MET ? [{ room: name, what: y > 0 ? 'moved down' : 'moved up', by: Math.abs(y) }] : []),
    ];
    const resized = parameters.flatMap((parameter, index) =>
      parameter.room === room && parameter.isLength && values[index] !== initial[index]
        ? [{ room: name, what: parameter.what, by: values[index] - initial[index] }]
        : []
    );
    return [...moved, ...resized];
  }).sort((a, b) => Math.abs(b.by) - Math.abs(a.by));
}

/**
 * The feature matches held that weren't already holding at the start. Those
 * stay in the solve, so nothing pulls them apart, but only the rest count as
 * lined up.
 */
const alignedCount = (problem: Problem, matches: Match[]) =>
  residualsOf(problem, problem.initial, matches).filter((miss, index) => isFeatureMatch(matches[index]) && Math.abs(miss) >= MET).length;

/** True up one level's placed rooms (see the file comment). */
export function trueUpRooms(rooms: readonly AssemblyRoom[]): TrueUpResult {
  const problem = trueUpProblem(rooms);
  const all = findMatches(linesOf(problem, problem.initial));
  const solution = pinToGrid(problem, spreadCorrections(problem, solveWithinLimit(problem, all)));
  const { matches } = solution;
  const values = featureCount(matches) ? solution.values : problem.initial;
  const adjusted = apply(problem, values);
  return {
    rooms: rooms.map((assemblyRoom, room) =>
      values.some((value, index) => problem.parameters[index].room === room && value !== problem.initial[index])
        ? { ...assemblyRoom, room: withFreeElementsFollowingWalls(assemblyRoom.room, adjusted[room]) }
        : assemblyRoom
    ),
    aligned: alignedCount(problem, matches),
    skipped: featureCount(all) - featureCount(matches),
    largestAdjustment: largestChange(problem, values),
    adjustments: listAdjustments(problem, values),
  };
}
